// Primitivas visuales de EL JARTI INGLÉS.
//
// Toda esta sección imita la estética de Amazon (cabecera azul marino, fondo
// gris claro, tarjetas blancas, botones amarillo/naranja de pastilla), así que
// NO usa las variables de tema de Jartinet (que es oscuro): los colores van
// escritos a mano para que la página se vea igual pase lo que pase con el tema.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Star } from 'lucide-react';

// ── Paleta ──────────────────────────────────────────────────────────────────
export const AMZ = {
  header: '#131921',
  subheader: '#232f3e',
  page: '#eaeded',
  card: '#ffffff',
  text: '#0f1111',
  muted: '#565959',
  link: '#007185',
  linkHover: '#c7511f',
  price: '#0f1111',
  deal: '#cc0c39',
  star: '#ffa41c',
  yellow: '#ffd814',
  yellowHover: '#f7ca00',
  orange: '#ffa41c',
  orangeHover: '#fa8900',
  stock: '#007600',
  border: '#d5d9d9',
} as const;

/** Tipografía de la sección: Amazon Ember no existe fuera de Amazon; Arial es lo que más se le parece. */
export const AMZ_FONT = 'Arial, "Helvetica Neue", Helvetica, sans-serif';

// ── Botones ─────────────────────────────────────────────────────────────────
type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'yellow' | 'orange' | 'ghost';
  block?: boolean;
};

export function AmzButton({ variant = 'yellow', block, className = '', style, ...rest }: BtnProps) {
  const base: React.CSSProperties = {
    borderRadius: 8,
    fontSize: 13,
    lineHeight: '29px',
    padding: '0 14px',
    border: '1px solid',
    cursor: rest.disabled ? 'not-allowed' : 'pointer',
    opacity: rest.disabled ? 0.55 : 1,
    width: block ? '100%' : undefined,
    fontFamily: AMZ_FONT,
    color: AMZ.text,
    whiteSpace: 'nowrap',
  };
  const variants: Record<string, React.CSSProperties> = {
    yellow: { background: AMZ.yellow, borderColor: '#fcd200', boxShadow: '0 2px 5px rgba(213,217,217,.5)' },
    orange: { background: AMZ.orange, borderColor: '#ff8f00', boxShadow: '0 2px 5px rgba(213,217,217,.5)' },
    ghost: { background: '#fff', borderColor: AMZ.border, boxShadow: '0 2px 5px rgba(213,217,217,.5)' },
  };
  return (
    <button
      {...rest}
      className={`transition-[filter] hover:brightness-95 active:brightness-90 ${className}`}
      style={{ ...base, ...variants[variant], ...style }}
    />
  );
}

/** Botón de pastilla (el redondeado de "Añadir a la cesta"). */
export function AmzPillButton(props: BtnProps) {
  return <AmzButton {...props} style={{ borderRadius: 999, lineHeight: '31px', ...props.style }} />;
}

// ── Precio en ORO con el formato de Amazon (símbolo pequeño arriba) ─────────
export function Price({ value, size = 'md', className = '' }: { value: number; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  const px = size === 'lg' ? 28 : size === 'md' ? 18 : 14;
  return (
    <span className={`inline-flex items-baseline ${className}`} style={{ color: AMZ.price, fontFamily: AMZ_FONT }}>
      <span style={{ fontSize: px * 0.55, transform: 'translateY(-0.35em)', marginRight: 1 }}>◈</span>
      <span style={{ fontSize: px, fontWeight: 400, letterSpacing: '-0.02em' }}>{n.toLocaleString('es-ES')}</span>
      <span style={{ fontSize: px * 0.5, marginLeft: 3, color: AMZ.muted }}>oro</span>
    </span>
  );
}

// ── Estrellas decorativas (no hay reseñas: es adorno de escaparate) ─────────
export function Stars({ value = 4.5, count }: { value?: number; count?: number }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="flex">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className="h-[14px] w-[14px]"
            style={{ color: AMZ.star }}
            fill={i + 0.5 <= value ? AMZ.star : i < value ? AMZ.star : 'transparent'}
            strokeWidth={1.5}
          />
        ))}
      </span>
      {count != null && <span style={{ color: AMZ.link, fontSize: 12 }}>{count.toLocaleString('es-ES')}</span>}
    </span>
  );
}

/** Puntuación estable derivada del id (siempre la misma para el mismo producto). */
export function fakeRating(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return { stars: 3.8 + (h % 13) / 10, reviews: 12 + (h % 940) };
}

// ── Fecha de entrega en cristiano ("hoy a las 19:40", "mañana a las 9:15") ──
export function formatEntrega(ms: number) {
  const d = new Date(ms);
  const hoy = new Date();
  const manana = new Date(hoy.getTime() + 86400000);
  const sameDay = (a: Date, b: Date) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, hoy)) return `hoy a las ${hora}`;
  if (sameDay(d, manana)) return `mañana a las ${hora}`;
  return `${d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} a las ${hora}`;
}

export function formatFecha(v: any) {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : (v?.toDate ? v.toDate() : new Date(v));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function toMs(v: any): number {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? 0 : t; }
  if (v?.toDate) return v.toDate().getTime();
  if (typeof v === 'number') return v;
  return 0;
}

// ── Carrito (estado local + localStorage) ───────────────────────────────────
export type CartLine = { productId: string; qty: number };

const CART_KEY = 'jarti_cart_v1';

function readCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((l) => l && l.productId) : [];
  } catch { return []; }
}

type CartCtx = {
  lines: CartLine[];
  count: number;
  add: (productId: string, qty?: number) => void;
  setQty: (productId: string, qty: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
};

const CartContext = createContext<CartCtx | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => (typeof window === 'undefined' ? [] : readCart()));

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(lines)); } catch { /* cuota llena: da igual */ }
  }, [lines]);

  const add = useCallback((productId: string, qty = 1) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.productId === productId);
      if (i < 0) return [...prev, { productId, qty: Math.max(1, qty) }];
      const copy = [...prev];
      copy[i] = { ...copy[i], qty: Math.min(99, copy[i].qty + qty) };
      return copy;
    });
  }, []);

  const setQty = useCallback((productId: string, qty: number) => {
    setLines((prev) => (qty <= 0
      ? prev.filter((l) => l.productId !== productId)
      : prev.map((l) => (l.productId === productId ? { ...l, qty: Math.min(99, qty) } : l))));
  }, []);

  const remove = useCallback((productId: string) => {
    setLines((prev) => prev.filter((l) => l.productId !== productId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartCtx>(
    () => ({ lines, count: lines.reduce((a, l) => a + l.qty, 0), add, setQty, remove, clear }),
    [lines, add, setQty, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartCtx {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart fuera de CartProvider');
  return ctx;
}

// ── Logotipo ────────────────────────────────────────────────────────────────
export function JartiInglesLogo({ small }: { small?: boolean }) {
  return (
    <span className="inline-flex select-none items-baseline" style={{ fontFamily: AMZ_FONT }}>
      <span style={{ fontSize: small ? 16 : 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>El Jarti</span>
      <span style={{ fontSize: small ? 16 : 20, fontWeight: 700, color: AMZ.orange, letterSpacing: '-0.02em', marginLeft: 4 }}>Inglés</span>
      <span
        aria-hidden
        style={{
          display: 'inline-block', width: small ? 22 : 28, height: 3, borderRadius: 3,
          background: AMZ.orange, marginLeft: -2, alignSelf: 'flex-end', marginBottom: 3,
        }}
      />
    </span>
  );
}

// ── Cabecera de sección gris (la barra de "categorías") ─────────────────────
export function SubNav({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: AMZ.subheader }} className="px-3 py-1.5">
      <div className="mx-auto flex max-w-[1500px] items-center gap-4 overflow-x-auto text-[13px] text-white/90">
        {children}
      </div>
    </div>
  );
}
